import React, { memo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PERSONALITY_INSIGHTS_ROUTE } from 'Utils/Constants';
import { selectDisclessPersonality } from 'Selector/GlobalSearchContentSelector';
import usePersonFollow from 'Utils/hooks/usePersonFollow';
import { getPersonListRowText } from 'Utils/personCard/getPersonCardTextLines';
import { PersonListRowProps } from 'Utils/personCard/mapExecutiveToPersonCardSimple';
import SmallButton from './SmallButton';
import {
  primaryFont,
  textBlack,
  wMedium,
  wMLight,
  noPersonImage,
  SecondaryColor,
  primaryHeadingColor,
  maxWidth,
  retina,
  maxTablet,
  maxMobile,
  minExtraLargeScreen,
} from '../../../Utils/utilsStyle';

export type PersonListRowSimpleProps = PersonListRowProps & {
  onRowClick?: () => void;
};

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  width: 100%;
  cursor: pointer;

  @media all and (min-width: ${minExtraLargeScreen}) {
    padding: 10px 0;
  }

  @media all and (max-width: ${maxWidth}), ${retina} {
    padding: 4px 0;
  }

  &:hover {
    background: #fff;
    border-radius: 6px;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoSection = styled.div`
  display: flex;
  column-gap: 10px;
  align-items: center;
  flex: 1;
  min-width: 0;

  @media all and (min-width: ${minExtraLargeScreen}) {
    column-gap: 14px;
  }

  @media all and (max-width: ${maxTablet}) {
    column-gap: 6px;
  }

  @media all and (max-width: ${maxMobile}) {
    column-gap: 8px;
  }
`;

const AvatarRing = styled.button<{ $color: string }>`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  padding: 2px;
  border: none;
  background: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  @media all and (min-width: ${minExtraLargeScreen}) {
    width: 54px;
    height: 54px;
  }

  @media all and (max-width: ${maxMobile}) {
    width: 36px;
    height: 36px;
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  background: #fff;
`;

const TextBlock = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const PersonName = styled.button`
  display: block;
  width: 100%;
  border: none;
  background: none;
  padding: 0;
  margin: 0 0 4px;
  color: ${textBlack};
  font-family: ${primaryFont};
  font-size: 16px;
  font-weight: ${wMedium};
  line-height: 1.125;
  letter-spacing: -0.52px;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media all and (min-width: ${minExtraLargeScreen}) {
    font-size: 22px;
    line-height: 1.05;
    letter-spacing: -0.99px;
    margin-bottom: 6px;
  }

  @media all and (max-width: ${maxTablet}) {
    font-size: 14px;
    margin-bottom: 2px;
  }

  @media all and (max-width: ${maxMobile}) {
    font-size: 12px;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${primaryHeadingColor};
  font-family: ${primaryFont};
  font-size: 14px;
  font-weight: ${wMLight};
  line-height: 1.14;
  letter-spacing: -0.35px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media all and (min-width: ${minExtraLargeScreen}) {
    font-size: 20px;
    line-height: 1.05;
    letter-spacing: -0.599px;
  }

  @media all and (max-width: ${maxTablet}) {
    font-size: 12px;
  }

  @media all and (max-width: ${maxMobile}) {
    font-size: 10px;
  }
`;

const FollowButtonWrap = styled.div`
  flex-shrink: 0;
  margin-left: 12px;

  .small-button-chinese {
    display: inline-flex;
    height: 24px;
    min-width: 84px;
    padding: 4px 14px;
    justify-content: center;
    align-items: center;
    border-radius: 16px;
    font-family: ${primaryFont};
    font-size: 14px;
    font-weight: ${wMLight};
    line-height: 17px;

    @media all and (min-width: ${minExtraLargeScreen}) {
      height: 34px;
      min-width: 120px;
      font-size: 20px;
      line-height: 23px;
      border-radius: 22px;
    }

    @media all and (max-width: ${maxTablet}) {
      height: 20px;
      min-width: 72px;
      padding: 3px 12px;
      font-size: 12px;
      line-height: 15px;
    }

    @media all and (max-width: ${maxMobile}) {
      min-width: 64px;
    }
  }
`;

const PersonListRowSimple = function PersonListRowSimple(props: PersonListRowSimpleProps) {
  const {
    personId,
    personName,
    personImage,
    personDesignation,
    headline,
    companyName,
    companyId,
    location = '',
    archetypeColor,
    personalityColor,
    personAdded,
    personType = 'EXECUTIVE',
    executiveType = 'EXECUTIVE',
    follow = true,
    noFollow = false,
    disableNavigation = false,
    openInNewTab = false,
    isSearchPage = false,
    isLivePerson = false,
    onRowClick,
  } = props;

  const navigate = useNavigate();
  const disclessPersonality = useSelector(selectDisclessPersonality);
  const ringColor = archetypeColor || personalityColor || '#979797';
  const profilePersonType = personType || executiveType;
  const profileUrl = `${PERSONALITY_INSIGHTS_ROUTE}${personId}&personType=${profilePersonType}`;

  const { disableButton, handleFollowClick } = usePersonFollow({
    personId,
    personName,
    personality: profilePersonType,
    personAdded,
    follow,
    isSearchPage,
    isLivePerson,
  });

  const openProfile = () => {
    if (disableNavigation) {
      return;
    }
    onRowClick?.();
    if (openInNewTab) {
      window.open(profileUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(profileUrl);
  };

  const { roleLine, companyLine } = getPersonListRowText({
    companyId,
    location,
    companyName,
    personDesignation,
    headline,
  });

  const subtitle = companyLine ? `${roleLine} | ${companyLine}` : roleLine;

  return (
    <Row className="person-list-row-simple">
      <InfoSection
        className="person-info"
        onClick={openProfile}
        onKeyDown={openProfile}
        role="button"
        tabIndex={0}
      >
        <AvatarRing
          type="button"
          $color={ringColor}
          onClick={(e) => {
            e.stopPropagation();
            openProfile();
          }}
          aria-label={`View ${personName} profile`}
        >
          <AvatarImage
            src={disclessPersonality ? noPersonImage : personImage}
            alt={personName}
          />
        </AvatarRing>
        <TextBlock className="personName">
          <PersonName
            type="button"
            className="person-name-here"
            title={personName}
            onClick={(e) => {
              e.stopPropagation();
              openProfile();
            }}
          >
            {personName}
          </PersonName>
          {subtitle ? (
            <Subtitle className="person-subtitle" title={subtitle}>
              {subtitle}
            </Subtitle>
          ) : null}
        </TextBlock>
      </InfoSection>

      {!noFollow ? (
        <FollowButtonWrap
          className="follow-c-btn"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          <SmallButton
            title={follow ? 'Follow' : 'Unfollow'}
            variant={follow ? 'filled' : 'outlined'}
            color={SecondaryColor}
            padding={follow ? '3px 11px' : '3px 5px'}
            onButtonClick={handleFollowClick}
            disabled={disableButton}
          />
        </FollowButtonWrap>
      ) : null}
    </Row>
  );
};

function areEqual(prev: PersonListRowSimpleProps, next: PersonListRowSimpleProps) {
  return prev.personId === next.personId
    && prev.personAdded === next.personAdded
    && prev.personImage === next.personImage
    && prev.personName === next.personName
    && prev.personDesignation === next.personDesignation
    && prev.headline === next.headline
    && prev.companyName === next.companyName
    && prev.companyId === next.companyId
    && prev.location === next.location
    && prev.personType === next.personType
    && prev.executiveType === next.executiveType
    && prev.openInNewTab === next.openInNewTab
    && prev.archetypeColor === next.archetypeColor
    && prev.follow === next.follow
    && prev.noFollow === next.noFollow;
}

export default memo(PersonListRowSimple, areEqual);
